import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { Check, Mountain, ArrowLeft, Crown } from "lucide-react";
import { toast } from "sonner";

export default function Pricing() {
    const { user, updateUser } = useAuth();
    const navigate = useNavigate();
    const [tiers, setTiers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [upgrading, setUpgrading] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/pricing/tiers");
                setTiers(data);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const handleChoose = async (tier) => {
        if (!user) { navigate("/login"); return; }
        if (tier.id === "free") {
            toast.success("You're on the Free tier");
            return;
        }
        setUpgrading(tier.id);
        try {
            const { data } = await api.post("/payments/helcim/initialize", { tier: tier.id });
            if (data.mocked) {
                updateUser({ tier: tier.id });
                toast.success(data.message);
                navigate("/feed");
            } else {
                // Real HelcimPay.js flow would open modal with data.checkoutToken
                toast.info("Opening payment modal…");
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setUpgrading(null);
        }
    };

    return (
        <div className="min-h-screen bg-alpine-mist">
            <header className="bg-midnight-pine text-white py-6">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
                    <Link to={user ? "/feed" : "/"} data-testid="pricing-back" className="inline-flex items-center gap-2 text-sm font-bold text-white/90 hover:text-ember-orange">
                        <ArrowLeft className="w-4 h-4" /> Back
                    </Link>
                    <Link to="/" className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-md bg-ember-orange flex items-center justify-center">
                            <Mountain className="w-5 h-5 text-white" />
                        </div>
                        <span className="font-display font-black text-xl">RealWorld Outdoors</span>
                    </Link>
                    <div className="w-20" />
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <div className="text-center mb-14">
                    <div className="text-xs font-bold uppercase tracking-[0.25em] text-ember-orange mb-3">Pricing</div>
                    <h1 className="font-display font-black text-5xl sm:text-6xl text-midnight-pine tracking-tighter leading-[0.95] mb-4">
                        Pick your altitude.
                    </h1>
                    <p className="text-lg text-slate-gray max-w-2xl mx-auto">
                        Start free. Upgrade when you want to post, sell, or get the full GPS toolkit.
                    </p>
                </div>

                {loading ? (
                    <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                        {[0, 1, 2].map((i) => (
                            <div key={i} className="h-96 bg-snowcap border border-ridge-line rounded-lg animate-pulse" />
                        ))}
                    </div>
                ) : (
                    <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                        {tiers.map((t) => {
                            const isCurrent = user?.tier === t.id;
                            const isPremium = t.id === "premium";
                            const isPopular = t.popular;
                            return (
                                <div
                                    key={t.id}
                                    className={`relative rounded-lg p-8 transition-all duration-300 hover:-translate-y-1 ${
                                        isPopular
                                            ? "bg-snowcap border-2 border-ember-orange shadow-xl"
                                            : isPremium
                                            ? "bg-midnight-pine text-white border border-deep-evergreen"
                                            : "bg-snowcap border border-ridge-line"
                                    }`}
                                >
                                    {isPopular && (
                                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-ember-orange text-white text-[10px] font-bold uppercase tracking-widest">
                                            Most popular
                                        </div>
                                    )}
                                    {isPremium && <Crown className="w-6 h-6 text-ember-orange mb-4" />}
                                    <div className="font-display font-black text-2xl mb-2">{t.name}</div>
                                    <p className={`text-sm mb-6 ${isPremium ? "text-white/70" : "text-slate-gray"}`}>
                                        {t.description}
                                    </p>
                                    <div className="font-display font-black text-5xl mb-6">
                                        ${t.price}
                                        <span className={`text-lg font-normal ${isPremium ? "text-white/60" : "text-slate-gray"}`}>
                                            /{t.period === "forever" ? "free" : "mo"}
                                        </span>
                                    </div>
                                    <ul className="space-y-3 mb-8">
                                        {t.features.map((f, i) => (
                                            <li key={i} className="flex items-start gap-2 text-sm">
                                                <Check className={`w-4 h-4 shrink-0 mt-0.5 ${isPremium ? "text-ember-orange" : "text-moss"}`} />
                                                <span className={isPremium ? "text-white/90" : "text-granite"}>{f}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <button
                                        onClick={() => handleChoose(t)}
                                        disabled={isCurrent || upgrading === t.id}
                                        data-testid={`pricing-cta-${t.id}`}
                                        className={`w-full py-3 rounded-md font-bold text-sm transition-all active:scale-95 ${
                                            isCurrent
                                                ? "bg-ridge-line text-slate-gray cursor-not-allowed"
                                                : isPopular || isPremium
                                                ? "bg-ember-orange text-white hover:bg-ember-dark"
                                                : "bg-midnight-pine text-white hover:bg-deep-evergreen"
                                        }`}
                                    >
                                        {isCurrent ? "Current plan" : upgrading === t.id ? "Upgrading…" : t.cta}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}

                <div className="mt-16 max-w-3xl mx-auto bg-trail-tan/40 border border-trail-tan rounded-lg p-6 text-center">
                    <div className="text-xs font-bold uppercase tracking-[0.25em] text-midnight-pine mb-2">Payments</div>
                    <p className="text-sm text-granite">
                        Payments are powered by <strong>Helcim</strong>. Add your <code className="px-1.5 py-0.5 bg-snowcap rounded text-xs">HELCIM_API_TOKEN</code> to the backend <code className="px-1.5 py-0.5 bg-snowcap rounded text-xs">.env</code> to enable real checkout. Until then, upgrades run in demo mode.
                    </p>
                </div>
            </div>
        </div>
    );
}
